import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Plus, Trash2, Download, Upload, FileText, FileType2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useConstants, labelFor, LABELS } from "@/lib/constants";

const emptyForm = {
  title: "",
  question: "",
  question_type: "mcq_single",
  options: ["", "", "", ""],
  correct_answer: 1,           // 1-based for single-select
  correct_answers: [],         // 1-based indices for multi-select
  expected_answer: "",
  accept_alternates: "",       // pipe-separated in form
  case_sensitive: false,
  numeric_answer: "",
  numeric_tolerance: "",
  date_answer: "",
  scenario_body: "",
  difficulty: "medium",
  topic: "",
  category: "",
  chapter: "",
  module_id: "",
  course_id: "",
  marks: 1,
  negative_marks: 0,
  explanation: "",
  reference_material: "",
  status: "active",
};

// Types that share the "compare expected text" answer flow.
const TEXT_ANSWER_TYPES = ["short_answer", "fill_blank"];
// Types that need a free-text scenario block (manual evaluation in Phase 2).
const SCENARIO_TYPES = ["clinical_scenario", "coding_scenario", "case_study", "long_answer", "essay"];

export default function QuestionBank() {
  const constants = useConstants();
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState({ category: "", chapter: "", question_type: "", module_id: "" });
  const [importing, setImporting] = useState(false);
  const [importingWord, setImportingWord] = useState(false);
  const fileInputRef = useRef(null);
  const wordInputRef = useRef(null);

  const load = async () => {
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) q.set(k, v); });
    const { data } = await api.get(`/questions?${q.toString()}`);
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data));
    api.get("/modules").then(r => setModules(r.data));
  }, []);

  // When opening the form, default course to the first available so module list lights up.
  const openForm = () => {
    setForm({ ...emptyForm, course_id: courses[0]?.id || "" });
    setOpen(true);
  };

  // Full question list for the currently-selected module (unfiltered by chapter/type/category)
  // — used to derive the narrowed Chapter dropdown so admins only see chapters that actually
  // have questions in the module they picked.
  const [allForModule, setAllForModule] = useState([]);
  useEffect(() => {
    if (!filter.module_id) { setAllForModule([]); return; }
    api.get(`/questions?module_id=${filter.module_id}`).then((r) => setAllForModule(r.data));
  }, [filter.module_id]);

  const chaptersForModule = useMemo(() => {
    if (!filter.module_id) return constants.chapters || [];
    const seen = new Set();
    allForModule.forEach((q) => { if (q.chapter) seen.add(q.chapter); });
    // Preserve the canonical ICD-10 order
    return (constants.chapters || []).filter((c) => seen.has(c));
  }, [filter.module_id, allForModule, constants.chapters]);

  const modulesForCourse = useMemo(
    () => modules.filter((m) => !form.course_id || m.course_id === form.course_id),
    [modules, form.course_id]
  );
  const moduleById = useMemo(
    () => Object.fromEntries(modules.map((m) => [m.id, m])), [modules]
  );

  const setOpt = (idx, val) => {
    setForm((f) => {
      const opts = [...f.options];
      opts[idx] = val;
      return { ...f, options: opts };
    });
  };
  const addOption = () => setForm((f) => f.options.length >= 8 ? f : { ...f, options: [...f.options, ""] });
  const removeOption = (idx) => setForm((f) => {
    if (f.options.length <= 2) return f;
    const opts = f.options.filter((_, i) => i !== idx);
    return {
      ...f,
      options: opts,
      correct_answer: f.correct_answer > opts.length ? 1 : f.correct_answer,
      correct_answers: f.correct_answers.filter((i) => i !== idx + 1)
        .map((i) => (i > idx + 1 ? i - 1 : i)),
    };
  });
  const toggleCorrectMulti = (n) => setForm((f) => ({
    ...f,
    correct_answers: f.correct_answers.includes(n)
      ? f.correct_answers.filter((i) => i !== n)
      : [...f.correct_answers, n].sort((a, b) => a - b),
  }));

  const create = async (e) => {
    e.preventDefault();
    const qt = form.question_type;
    const payload = {
      question: form.question,
      title: form.title || null,
      question_type: qt,
      topic: form.topic || "",
      category: form.category || null,
      chapter: form.chapter || null,
      module_id: form.module_id || null,
      course_id: form.course_id || null,
      marks: Number(form.marks) || 1,
      negative_marks: Number(form.negative_marks) || 0,
      explanation: form.explanation || null,
      reference_material: form.reference_material || null,
      status: form.status,
    };

    if (qt === "mcq_single" || qt === "mcq") {
      const opts = form.options.filter((o) => o.trim() !== "");
      if (opts.length < 2) return toast.error("MCQ needs at least 2 options");
      payload.options = opts;
      payload.correct_answer = Math.min(Number(form.correct_answer) || 1, opts.length);
      // Backwards-compat fill option1..4
      for (let i = 0; i < Math.min(opts.length, 4); i++) payload[`option${i + 1}`] = opts[i];
    } else if (qt === "mcq_multi") {
      const opts = form.options.filter((o) => o.trim() !== "");
      if (opts.length < 2) return toast.error("Multi-select needs at least 2 options");
      if (form.correct_answers.length === 0) return toast.error("Pick at least one correct option");
      payload.options = opts;
      payload.correct_answers = form.correct_answers.filter((i) => i <= opts.length);
    } else if (qt === "true_false") {
      payload.options = ["True", "False"];
      payload.correct_answer = Number(form.correct_answer) === 1 ? 1 : 2;
    } else if (qt === "yes_no") {
      payload.options = ["Yes", "No"];
      payload.correct_answer = Number(form.correct_answer) === 1 ? 1 : 2;
    } else if (TEXT_ANSWER_TYPES.includes(qt)) {
      const alts = (form.accept_alternates || "").split("|").map((s) => s.trim()).filter(Boolean);
      payload.expected_answer = form.expected_answer;
      payload.accept_alternates = alts;
      payload.case_sensitive = !!form.case_sensitive;
    } else if (qt === "numeric") {
      payload.numeric_answer = form.numeric_answer !== "" ? Number(form.numeric_answer) : null;
      payload.numeric_tolerance = form.numeric_tolerance !== "" ? Number(form.numeric_tolerance) : 0;
    } else if (qt === "date_answer") {
      payload.date_answer = form.date_answer || null;
    } else if (SCENARIO_TYPES.includes(qt)) {
      payload.scenario_body = form.scenario_body || "";
      // Expected answer is optional rubric notes for manual evaluation
      payload.expected_answer = form.expected_answer || "";
    }

    try {
      await api.post("/questions", payload);
      toast.success("Question added");
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create question");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete question?")) return;
    await api.delete(`/questions/${id}`);
    load();
  };

  // Export / Import (filter-aware)
  const exportCsv = () => {
    const q = new URLSearchParams();
    if (filter.category) q.set("category", filter.category);
    if (filter.module_id) q.set("module_id", filter.module_id);
    if (filter.question_type) q.set("question_type", filter.question_type);
    window.location.href = `${API_BASE}/questions/export?${q.toString()}`;
  };
  const downloadTemplate = () => { window.location.href = `${API_BASE}/questions/template`; };
  const downloadWordTemplate = () => { window.location.href = `${API_BASE}/questions/word-template`; };
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) return toast.error("Please pick a .csv file");
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/questions/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} question${data.inserted === 1 ? "" : "s"}` +
        (data.skipped ? ` · ${data.skipped} skipped` : ""));
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Import failed"));
    } finally { setImporting(false); }
  };

  const onImportWord = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".doc") && !name.endsWith(".docx")) {
      return toast.error("Please pick a .doc or .docx file");
    }
    const fd = new FormData(); fd.append("file", file);
    setImportingWord(true);
    try {
      const { data } = await api.post("/questions/import-word", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} question${data.inserted === 1 ? "" : "s"} from Word` +
        (data.skipped ? ` · ${data.skipped} skipped` : ""));
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Word import failed");
      toast.error(msg);
      if (detail?.hint) toast.message(detail.hint);
    } finally { setImportingWord(false); }
  };

  return (
    <div>
      <PageHeader
        title="Question Bank"
        subtitle="Author and manage questions across all supported types."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onImport}
              className="hidden" data-testid="question-import-input" />
            <input ref={wordInputRef} type="file" accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onImportWord} className="hidden" data-testid="question-import-word-input" />
            <button type="button" onClick={downloadTemplate} data-testid="question-template-button"
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <FileText className="w-4 h-4" /> Template
            </button>
            <button type="button" onClick={exportCsv} data-testid="question-export-button"
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="question-import-button"
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60">
              <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import CSV"}
            </button>
            <button type="button" onClick={() => wordInputRef.current?.click()} disabled={importingWord}
              data-testid="question-import-word-button"
              title="Import questions from a .docx file (Q:/A)B)C)D)/Answer: format)"
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-indigo-400 hover:bg-indigo-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60">
              <FileType2 className="w-4 h-4 text-indigo-600" /> {importingWord ? "Importing…" : "Import from Word"}
            </button>
            <button type="button" onClick={downloadWordTemplate}
              data-testid="question-word-template-button"
              title="Download a .docx template showing the accepted question formats"
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-indigo-400 hover:bg-indigo-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <FileType2 className="w-4 h-4 text-indigo-600" /> Word template
            </button>
            <button data-testid="question-add-button" onClick={openForm}
              className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Add question
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          className={inputCls} data-testid="filter-category">
          <option value="">All categories</option>
          {constants.question_categories?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.chapter} onChange={(e) => setFilter({ ...filter, chapter: e.target.value })}
          className={inputCls} data-testid="filter-chapter">
          <option value="">
            {filter.module_id
              ? (chaptersForModule.length === 0 ? "No chapters in this module" : "All chapters (in module)")
              : "All chapters"}
          </option>
          {chaptersForModule.map((c) => (
            <option key={c} value={c} title={c}>
              {((constants.chapters || []).indexOf(c) + 1)}. {c.length > 60 ? c.slice(0, 57) + "…" : c}
            </option>
          ))}
        </select>
        <select value={filter.question_type} onChange={(e) => setFilter({ ...filter, question_type: e.target.value })}
          className={inputCls} data-testid="filter-type">
          <option value="">All types</option>
          {constants.question_types?.filter((t) => t !== "mcq").map((t) =>
            <option key={t} value={t}>{labelFor("question_types", t)}</option>)}
        </select>
        <select value={filter.module_id} onChange={(e) => setFilter({ ...filter, module_id: e.target.value, chapter: "" })}
          className={inputCls} data-testid="filter-module">
          <option value="">All modules</option>
          {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {open && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
          <form onSubmit={create} className="space-y-5">
            {/* Meta row */}
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Question type">
                <select data-testid="question-type-select" value={form.question_type}
                  onChange={(e) => setForm({ ...form, question_type: e.target.value })} className={inputCls}>
                  {(constants.question_types || []).filter((t) => t !== "mcq").map((t) => (
                    <option key={t} value={t}>{labelFor("question_types", t)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={inputCls}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </Field>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Course">
                <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value, module_id: "" })}
                  className={inputCls}>
                  <option value="">— Any —</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.course_name}</option>)}
                </select>
              </Field>
              <Field label="Module">
                <select value={form.module_id} onChange={(e) => setForm({ ...form, module_id: e.target.value })}
                  className={inputCls} data-testid="question-module-select">
                  <option value="">— None —</option>
                  {modulesForCourse.map((m) => <option key={m.id} value={m.id}>{m.order}. {m.name}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className={inputCls} data-testid="question-category-select">
                  <option value="">— None —</option>
                  {(constants.question_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid md:grid-cols-1 gap-4">
              <Field label="Chapter (ICD-10-CM)">
                <select value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })}
                  className={inputCls} data-testid="question-chapter-select">
                  <option value="">— None —</option>
                  {(constants.chapters || []).map((c, i) => (
                    <option key={c} value={c}>{i + 1}. {c}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Marks">
                <input type="number" min={0} step="0.5" value={form.marks}
                  onChange={(e) => setForm({ ...form, marks: e.target.value })} className={inputCls}
                  data-testid="question-marks-input" />
              </Field>
              <Field label="Negative marks">
                <input type="number" min={0} step="0.25" value={form.negative_marks}
                  onChange={(e) => setForm({ ...form, negative_marks: e.target.value })} className={inputCls}
                  data-testid="question-negmarks-input" />
              </Field>
              <Field label="Topic / Tag">
                <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className={inputCls} placeholder="optional free-text tag" />
              </Field>
            </div>

            <Field label="Question title (optional)">
              <input data-testid="question-title-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={inputCls} placeholder="e.g. PDGM Episode Reimbursement" />
            </Field>

            <Field label={`Question ${form.question_type === "fill_blank" ? "(use _____ for the blank)" : ""}`}>
              <textarea required rows={2} value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                className={inputCls} data-testid="question-text-input" />
            </Field>

            {/* Type-specific answer builder */}
            {(form.question_type === "mcq_single" || form.question_type === "mcq_multi" || form.question_type === "mcq") && (
              <McqOptions form={form} setOpt={setOpt} addOption={addOption}
                removeOption={removeOption} setForm={setForm}
                multi={form.question_type === "mcq_multi"} toggleCorrectMulti={toggleCorrectMulti} />
            )}

            {(form.question_type === "true_false" || form.question_type === "yes_no") && (
              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">Correct answer</div>
                <div className="flex gap-4">
                  {(form.question_type === "true_false" ? ["True", "False"] : ["Yes", "No"]).map((label, i) => (
                    <label key={label} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input type="radio" name="tf" checked={Number(form.correct_answer) === i + 1}
                        onChange={() => setForm({ ...form, correct_answer: i + 1 })}
                        className="w-4 h-4 accent-blue-600" data-testid={`tf-correct-${i}`} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {TEXT_ANSWER_TYPES.includes(form.question_type) && (
              <>
                <Field label="Expected answer">
                  <input required data-testid="question-expected-input" value={form.expected_answer}
                    onChange={(e) => setForm({ ...form, expected_answer: e.target.value })}
                    className={inputCls} />
                </Field>
                <Field label="Accepted alternatives (optional, pipe-separated)">
                  <input data-testid="question-alternates-input" value={form.accept_alternates}
                    onChange={(e) => setForm({ ...form, accept_alternates: e.target.value })}
                    className={inputCls} placeholder="e.g. Paris | paris | PARIS" />
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.case_sensitive}
                    onChange={(e) => setForm({ ...form, case_sensitive: e.target.checked })}
                    className="w-4 h-4 accent-blue-600" />
                  Case-sensitive matching
                </label>
              </>
            )}

            {form.question_type === "numeric" && (
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Numeric answer">
                  <input required type="number" step="any" value={form.numeric_answer}
                    onChange={(e) => setForm({ ...form, numeric_answer: e.target.value })}
                    className={inputCls} data-testid="question-numeric-input" />
                </Field>
                <Field label="± Tolerance (optional)">
                  <input type="number" step="any" min={0} value={form.numeric_tolerance}
                    onChange={(e) => setForm({ ...form, numeric_tolerance: e.target.value })}
                    className={inputCls} placeholder="e.g. 0.5" />
                </Field>
              </div>
            )}

            {form.question_type === "date_answer" && (
              <Field label="Date answer">
                <input required type="date" value={form.date_answer}
                  onChange={(e) => setForm({ ...form, date_answer: e.target.value })} className={inputCls} />
              </Field>
            )}

            {SCENARIO_TYPES.includes(form.question_type) && (
              <>
                <Field label="Scenario / case content (shown to candidate)">
                  <textarea rows={5} value={form.scenario_body}
                    onChange={(e) => setForm({ ...form, scenario_body: e.target.value })}
                    className={inputCls} placeholder="Paste referral notes, SOC, OASIS, clinical notes…" />
                </Field>
                <Field label="Rubric / model answer (for manual evaluation)">
                  <textarea rows={3} value={form.expected_answer}
                    onChange={(e) => setForm({ ...form, expected_answer: e.target.value })}
                    className={inputCls} />
                </Field>
              </>
            )}

            <Field label="Explanation (optional, shown after submission)">
              <textarea rows={2} value={form.explanation}
                onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                className={inputCls} placeholder="Why is this the correct answer?" />
            </Field>
            <Field label="Reference material (optional)">
              <input value={form.reference_material}
                onChange={(e) => setForm({ ...form, reference_material: e.target.value })}
                className={inputCls} placeholder="Link to guideline / textbook chapter" />
            </Field>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={() => setOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button data-testid="question-create-submit" type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700">
                Create question
              </button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No questions" hint="Add your first question to start." />
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <QuestionCard key={q.id} q={q} moduleById={moduleById} onDelete={() => del(q.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function McqOptions({ form, setOpt, addOption, removeOption, setForm, multi, toggleCorrectMulti }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-slate-700">
          Options ({form.options.length} of 2–8) — {multi ? "tick all correct" : "select the single correct option"}
        </div>
        <button type="button" onClick={addOption}
          disabled={form.options.length >= 8}
          className="text-sm text-blue-600 hover:text-blue-700 disabled:text-slate-300">+ Add option</button>
      </div>
      <div className="space-y-2">
        {form.options.map((opt, idx) => (
          <div key={idx} className="flex items-center gap-2">
            {multi ? (
              <input type="checkbox" checked={form.correct_answers.includes(idx + 1)}
                onChange={() => toggleCorrectMulti(idx + 1)}
                className="w-4 h-4 accent-blue-600" data-testid={`mcq-correct-${idx}`} />
            ) : (
              <input type="radio" name="mcq-correct"
                checked={Number(form.correct_answer) === idx + 1}
                onChange={() => setForm((f) => ({ ...f, correct_answer: idx + 1 }))}
                className="w-4 h-4 accent-blue-600" data-testid={`mcq-correct-${idx}`} />
            )}
            <input value={opt} onChange={(e) => setOpt(idx, e.target.value)}
              required={idx < 2}
              className={inputCls + " flex-1"} placeholder={`Option ${idx + 1}`}
              data-testid={`question-option${idx + 1}-input`} />
            <button type="button" onClick={() => removeOption(idx)} disabled={form.options.length <= 2}
              className="text-slate-400 hover:text-red-600 disabled:opacity-30">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionCard({ q, moduleById, onDelete }) {
  const qt = q.question_type || "mcq_single";
  const opts = q.options?.length ? q.options : [q.option1, q.option2, q.option3, q.option4].filter(Boolean);
  return (
    <div data-testid={`question-card-${q.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {q.title && <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{q.title}</div>}
          <div className="text-sm text-slate-900 font-medium">{q.question}</div>

          {(qt === "mcq" || qt === "mcq_single" || qt === "mcq_multi" || qt === "true_false" || qt === "yes_no") && (
            <div className="mt-2 grid md:grid-cols-2 gap-1.5 text-sm">
              {(opts.length ? opts : (qt === "true_false" ? ["True", "False"] : qt === "yes_no" ? ["Yes", "No"] : []))
                .map((o, i) => {
                  const isCorrect = (q.correct_answers?.includes(i + 1)) || q.correct_answer === i + 1;
                  return (
                    <div key={i}
                      className={`px-3 py-1.5 rounded border ${isCorrect ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700"}`}>
                      <span className="text-xs font-mono-acc text-slate-500 mr-2">{i + 1}</span>{o}
                    </div>
                  );
                })}
            </div>
          )}

          {(qt === "short_answer" || qt === "fill_blank") && (
            <div className="mt-2 text-sm text-slate-700">
              <span className="text-xs uppercase tracking-wider text-slate-500 mr-2">Answer:</span>
              <span className="font-medium text-emerald-700">{q.expected_answer}</span>
              {q.accept_alternates?.length > 0 && (
                <span className="text-slate-500 ml-2">(also: {q.accept_alternates.join(", ")})</span>
              )}
            </div>
          )}

          {qt === "numeric" && (
            <div className="mt-2 text-sm text-slate-700">
              <span className="text-xs uppercase tracking-wider text-slate-500 mr-2">Answer:</span>
              <span className="font-medium text-emerald-700">{q.numeric_answer}</span>
              {q.numeric_tolerance ? <span className="text-slate-500 ml-2">± {q.numeric_tolerance}</span> : null}
            </div>
          )}
          {qt === "date_answer" && (
            <div className="mt-2 text-sm text-slate-700">
              <span className="text-xs uppercase tracking-wider text-slate-500 mr-2">Answer:</span>
              <span className="font-medium text-emerald-700">{q.date_answer}</span>
            </div>
          )}
          {(qt === "clinical_scenario" || qt === "coding_scenario" || qt === "case_study" || qt === "long_answer" || qt === "essay") && q.scenario_body && (
            <div className="mt-2 text-xs text-slate-500 line-clamp-3">{q.scenario_body}</div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Pill tone="slate">{LABELS.question_types[qt] || qt}</Pill>
            {q.category && <Pill tone="blue">{q.category}</Pill>}
            {q.chapter && <Pill tone="purple" title={q.chapter}>Ch: {q.chapter.length > 40 ? q.chapter.slice(0, 40) + "…" : q.chapter}</Pill>}
            {q.module_id && moduleById[q.module_id] && <Pill tone="indigo">{moduleById[q.module_id].name}</Pill>}
            {q.marks ? <Pill tone="slate">{q.marks} mk{q.marks === 1 ? "" : "s"}</Pill> : null}
            {q.status && q.status !== "active" && <Pill tone="amber">{q.status}</Pill>}
          </div>
        </div>
        <button data-testid={`question-delete-${q.id}`} onClick={onDelete}
          className="text-red-600 hover:bg-red-50 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
